// ------------------------------------------------------------
// Date, ID, and URL Utility Functions
// ------------------------------------------------------------

/**
 * getTodayDateString
 * ------------------------------------------------------------
 * Returns today's date in 'YYYY-MM-DD' format (local timezone)
 * @returns {string} e.g., '2025-09-08'
 */
export function getTodayDateString() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * getNowTimeString
 * ------------------------------------------------------------
 * Returns current time in 'HH:MM' format (local timezone)
 * @returns {string} e.g., '14:35'
 */
export function getNowTimeString() {
  const d = new Date();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * formatLastChangedDate
 * ------------------------------------------------------------
 * Formats an ISO date string into 'dd/MM/yy' and prepends a label
 * @param {string} isoString - ISO date string
 * @param {string} label - Optional label (default: 'Last changed')
 * @returns {string} e.g., 'Last changed 08/09/25'
 */
export function formatLastChangedDate(isoString, label = "Last changed") {
  if (!isoString) return "";

  const formatted = new Date(isoString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

  return `${label} ${formatted}`;
}

/**
 * formatLastChangedDate
 * ------------------------------------------------------------
 * Formats an ISO date string into 'dd/MM/yy' format and prepends a label.
 *
 * @param {string} isoString - The ISO date string to format, e.g., "2025-09-08T00:00:00.000Z".
 * @param {string} [label='Last changed'] - Optional label to prepend before the formatted date.
 * @returns {string} Formatted string, e.g., 'Last changed 08/09/25'.
 *
 * @example
 */

export function formatDateToYYYYMMDD(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // 0-indexed
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * formatFullDateTime
 * ------------------------------------------------------------
 * Formats an ISO date string into 'dd Mon yyyy, h:mm AM/PM' format
 * @param {string} isoString - ISO date string
 * @param {string} label - Optional label to prepend
 * @returns {string} e.g., 'Updated 08 Sep 2025, 2:35 PM'
 */
export function formatFullDateTime(isoString, label = "") {
  if (!isoString) return "";

  const formatted = new Date(isoString)
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata", // Adjust as needed
    })
    .replace("am", "AM")
    .replace("pm", "PM");

  return label ? `${label} ${formatted}` : formatted;
}

/**
 * formatDateTimeTwoDigit
 * ------------------------------------------------------------
 * Formats an ISO date string to 'dd/MM/yy, HH:MM AM/PM' format
 * @param {string} isoString - ISO date string
 * @returns {string} e.g., '08/09/25, 02:35 PM'
 */
export const formatDateTimeTwoDigit = (isoString) => {
  const date = new Date(isoString);

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * generateUniqueId
 * ------------------------------------------------------------
 * Generates a unique ID based on current timestamp in seconds
 * @returns {string} e.g., '1694160935'
 */
export function generateUniqueId() {
  const timestamp = Math.floor(Date.now() / 1000); // current time in seconds
  return `${timestamp}`;
}

export function formatTimeToHHMMA(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";

  hours = hours % 12 || 12; // convert 0 → 12 for 12-hour format

  return `${hours}:${minutes}${ampm}`;
}

/**
 * getFileNameFromUrl
 * ------------------------------------------------------------
 * Extracts the file name from a given URL
 * @param {string} url - The full URL of the file
 * @returns {string} e.g., 'image.png'
 */
export function getFileNameFromUrl(url) {
  const parsedUrl = new URL(url);
  const pathname = parsedUrl.pathname;
  return pathname.substring(pathname.lastIndexOf("/") + 1);
}

/**
 * Formats a date of birth (DOB) into "YYYY-MM-DD (X years)" format.
 * Calculates age in full years based on current date.
 *
 * @param {string | Date} date - The date of birth to format
 * @returns {string} Formatted DOB with age, or original input if invalid
 */
export const formatDobWithAge = (date) => {
  let dobText = "";

  if (date) {
    const d = new Date(date);

    if (!isNaN(d.getTime())) {
      const now = new Date();

      // Calculate full years difference
      let years = now.getFullYear() - d.getFullYear();
      const monthDiff = now.getMonth() - d.getMonth();

      // Adjust if birthday hasn't occurred yet this year
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) {
        years--;
      }

      // Format date as YYYY-MM-DD
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");

      dobText = `${yyyy}-${mm}-${dd} (${years} year${years === 1 ? "" : "s"})`;
    } else {
      dobText = String(date); // Return original if invalid date
    }
  }

  return dobText;
};

/**
 * Formats a date string or Date object into "D.M.YYYY, HH:mm:ss"
 *
 * @param {string | Date} date - The input date
 * @returns {string} Formatted date string
 */
export const formatDateTime = (date) => {
  if (!date) return "";

  const d = new Date(date);
  if (isNaN(d.getTime())) return String(date); // Return original if invalid

  const day = d.getDate();
  const month = d.getMonth() + 1; // Months are zero-based
  const year = d.getFullYear();

  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");

  return `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
};

export const epochToDate = (epoch, options = {}) => {
  if (!epoch) return null;

  const date = new Date(epoch * 1000); // epoch in seconds

  return date.toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...options,
  });
};

export function formatFinalDateTime(date) {
  if (!date) return "---";

  // 🔥 Convert string to Date
  const d = new Date(date);

  if (isNaN(d)) return "---"; // optional safety check

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export const formatLocalDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// For telemetry data
// export const todayYMD = () => {
//   return new Date().toISOString().slice(0, 10);
// };
export function todayYMD() {
  return ymdOfLocal(new Date());
}
export function compareYMD(a, b) {
  const da = new Date(a + "T00:00:00");
  const db = new Date(b + "T00:00:00");

  if (da.getTime() === db.getTime()) return 0;
  return da.getTime() < db.getTime() ? -1 : 1;
}

// export const compareYMD = (a, b) => {
//   if (a === b) return 0;
//   return a > b ? 1 : -1;
// };

export const addDaysYMD = (ymd, days) => {
  const d = new Date(ymd);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export const formatDateToLabel = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// for gateway graph

export function safeParseDate(raw) {
  const s = String(raw || "");
  const safe =
    s.includes(".") && s.split(".")[1]?.length > 3
      ? s.replace(/(\.\d{3})\d+/, "$1")
      : s;

  const d = new Date(safe);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function startOfDayLocal(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function ymdOfLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isFutureDateOnly(trackingDateYMD) {
  const selected = safeParseDate(`${trackingDateYMD}T00:00:00`);
  if (!selected) return true;
  const today = startOfDayLocal(new Date());
  return startOfDayLocal(selected).getTime() > today.getTime();
}

// export function addDaysYMD(ymd, deltaDays) {
//   const d = safeParseDate(`${ymd}T00:00:00`);
//   if (!d) return ymd;
//   d.setDate(d.getDate() + deltaDays);
//   return ymdOfLocal(d);
// }

// export function compareYMD(a, b) {
//   if (a === b) return 0;
//   return a < b ? -1 : 1;
// }
export const HOURS_12 = Array.from({ length: 13 }, (_, i) => i * 2); // 0..24 step 2
export const hourTicks = Array.from({ length: 25 }, (_, i) => i);

/**
 * Signal strength categorization - ONLY for connectivity dots
 */
export function getSignalCategory(signal) {
  if (signal == null) return null;
  if (signal <= -85) return "no";
  if (signal <= -75) return "weak";
  return "good";
}

export function getSignalColor(category) {
  if (category === "no") return "#dc2626";
  if (category === "weak") return "#eab308";
  if (category === "good") return "#16a34a";
  return "#9ca3af";
}

/**
 * Match image values: 130, 70, 35, 0
 */
export function calculateDataPoints(signal) {
  if (signal == null) return null;
  if (signal <= -85) return 0;
  if (signal <= -75) return 35;
  if (signal <= -65) return 70;
  return 130;
}
// for eartag tele data
// helper function
// const todayYMD = new Date().toISOString().slice(0, 10);

// const compareYMD = (a, b) => {
//   if (a === b) return 0;
//   return a > b ? 1 : -1;
// };

// const addDaysYMD = (ymd, days) => {
//   const d = new Date(ymd);
//   d.setDate(d.getDate() + days);
//   return d.toISOString().slice(0, 10);
// };
// const formatDateToLabel = (date) => {
//   const day = String(date.getDate()).padStart(2, "0");
//   const month = date.toLocaleString("en-US", { month: "short" });
//   const year = date.getFullYear();
//   return `${day}-${month}-${year}`; // e.g. 19-Jan-2026
// };

// for eartag graph data
/** -------- helpers -------- */
export function toDateObj(dateStr) {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(dateStr, deltaDays) {
  const d = toDateObj(dateStr);
  d.setDate(d.getDate() + deltaDays);
  return toYMD(d);
}

export function isFutureDate(dateStr) {
  const d = toDateObj(dateStr);
  const today = new Date();
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  return d.getTime() > todayOnly.getTime();
}

/** -------- constants -------- */
export const CHECKPOINTS = [2, 8, 14, 20];
export const WINDOW_SIZE_HOURS = 6;
export const INTERVAL_COLORS = {
  green: "#16a34a",
  yellow: "#eab308",
  red: "#dc2626",
};
export const CHECKPOINT_DOT_GREEN = "#16a34a";

/** Compute total points */
export function computeTotalPoints(d) {
  const run = d?.run ?? 0;
  const steps = d?.steps ?? 0;
  const still = d?.still ?? 0;
  return run + steps + still;
}

/**
 * formatISOStringToSQLDateTime
 * ------------------------------------------------------------
 * Converts an ISO date string into SQL-compatible format:
 * 'YYYY-MM-DD HH:mm:ss'
 *
 * @param {string} isoString - ISO date string from backend/API/DB
 * @param {string} label - Optional text to prepend (e.g. "Updated")
 * @returns {string} - SQL-friendly date-time string
 */
export function formatISOStringToSQLDateTime(isoString, label = "") {
  // Return empty string if no valid date is provided
  if (!isoString) return "";
  const date = new Date(isoString);

  const isoUTC = date.toISOString();
  const withoutMilliseconds = isoUTC.slice(0, 19);
  const formatted = withoutMilliseconds.replace("T", " ");
  return label ? `${label} ${formatted}` : formatted;
}

export const epochToDateTime = (epoch, options = {}) => {
  if (!epoch) return null;

  const date = new Date(epoch * 1000);

  const pad = (n) => String(n).padStart(2, "0");

  const dd = pad(date.getDate());
  const mm = pad(date.getMonth() + 1);
  const yy = String(date.getFullYear()).slice(-2);

  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  const ss = pad(date.getSeconds());

  return `${dd}-${mm}-${yy}, ${hh}:${min}:${ss}`;
};
export const formatDMSValue = (value) => {
  if (value == null) return "-";

  const abs = Math.abs(value);

  const degrees = Math.floor(abs);
  const minutesFull = (abs - degrees) * 60;
  const minutes = Math.floor(minutesFull);
  const seconds = ((minutesFull - minutes) * 60).toFixed(3);

  return `${degrees}° ${minutes}' ${seconds}"`;
};

export function formatDateTimeInHM(date) {
  if (!date) return "---";

  const d = new Date(date);

  if (isNaN(d.getTime())) return "---";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year}, ${hours}:${minutes}`;
}
